import { useState } from "react";
import { toast } from "sonner";
import { Plus, Receipt, Trash2, Wallet, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ChargeBadge } from "@/components/StatusBadge";
import { KpiCard, Empty } from "@/components/KpiCard";
import { PaymentDialog } from "@/components/PaymentDialog";
import { PlanDialog } from "@/components/PlanDialog";
import { WhatsAppDialog } from "@/components/WhatsAppDialog";
import { useApi, useResource, errMsg, notifyRefresh } from "@/lib/api";
import { METHODS, brl, fmtDate } from "@/lib/constants";
import { useSettings } from "@/lib/settings";

const printReceipt = (p, pay, s) => {
  const w = window.open("", "_blank");
  if (!w) return toast.error("Permita pop-ups para imprimir o recibo");
  w.document.write(`<html><head><title>Recibo</title></head><body style="font-family:sans-serif;padding:40px;max-width:640px;margin:auto">
    <h2 style="color:#0369A1">${s.clinic_name || "Consultório odontológico"}</h2>
    <p>${s.dentist_name || ""} ${s.cro ? "· " + s.cro : ""}</p><hr/>
    <h1>RECIBO · ${brl(pay.amount)}</h1>
    <p>Recebi de <b>${p.name}</b>${p.cpf ? ` (CPF ${p.cpf})` : ""} a importância de <b>${brl(pay.amount)}</b>,
    via <b>${METHODS[pay.method]}</b>, em ${fmtDate(pay.date)}, referente a tratamento odontológico.</p>
    ${pay.notes ? `<p>Obs.: ${pay.notes}</p>` : ""}
    <br/><br/><p>_______________________________<br/>${s.dentist_name || "Assinatura"}</p>
    <script>window.print()</script></body></html>`);
  w.document.close();
};

export const FinanceTab = ({ p }) => {
  const api = useApi();
  const s = useSettings();
  const { data } = useResource(`/patients/${p.id}/finance`);
  const [payCharge, setPayCharge] = useState(undefined);
  const [plan, setPlan] = useState(false);
  const [wa, setWa] = useState(false);

  if (!data) return null;
  const { summary, charges, payments } = data;
  const pct = summary.total ? Math.min(100, (summary.paid / summary.total) * 100) : 0;
  const nextOpen = charges.find((c) => c.status !== "pago");

  const del = async (url, msg) => {
    if (!window.confirm(msg)) return;
    try { await api.delete(url); notifyRefresh(); toast.success("Removido"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Total contratado" value={brl(summary.total)} icon={Wallet} testId="finance-total" />
        <KpiCard label="Já pago" value={brl(summary.paid)} tone="emerald" testId="finance-paid" />
        <KpiCard label="Resta pagar" value={brl(summary.remaining)} tone="sky" testId="finance-remaining" />
        <KpiCard label="Em atraso" value={brl(summary.overdue)} tone="rose" testId="finance-overdue" />
      </div>
      <div className="surface p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-[200px] flex-1">
            <p className="text-sm font-semibold text-slate-700">Progresso de pagamento · {pct.toFixed(0)}%</p>
            <Progress value={pct} className="mt-2 h-2.5 bg-sky-100" data-testid="finance-progress" />
          </div>
          <div className="flex flex-wrap gap-2">
            {summary.overdue > 0 && (
              <Button variant="outline" className="rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => setWa(true)} data-testid="finance-whatsapp-btn">
                <MessageCircle className="mr-1.5 h-4 w-4" /> Lembrar pagamento
              </Button>
            )}
            <Button variant="outline" className="rounded-full" onClick={() => setPayCharge(null)} data-testid="finance-add-payment-btn"><Plus className="mr-1 h-4 w-4" /> Pagamento avulso</Button>
            <Button className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={() => setPlan(true)} data-testid="finance-add-plan-btn"><Plus className="mr-1 h-4 w-4" /> Novo orçamento</Button>
          </div>
        </div>
      </div>
      <div className="grid gap-5 xl:grid-cols-[1.4fr_1fr]">
        <div className="surface overflow-hidden">
          <h3 className="font-display px-5 pt-5 text-lg font-bold text-slate-900">Parcelas e mensalidades</h3>
          <div className="mt-3 overflow-x-auto">
            {charges.length === 0 ? <div className="p-5"><Empty>Nenhum orçamento lançado</Empty></div> : (
              <table className="w-full text-sm" data-testid="charges-table">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
                  <tr><th className="px-5 py-2.5">Descrição</th><th className="px-3">Vencimento</th><th className="px-3 text-right">Valor</th><th className="px-3">Status</th><th className="px-3"></th></tr>
                </thead>
                <tbody>
                  {charges.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100" data-testid={`charge-row-${c.id}`}>
                      <td className="px-5 py-3 font-medium text-slate-700">{c.description}{c.paid > 0 && c.status !== "pago" && <span className="block text-xs text-slate-400">pago {brl(c.paid)}</span>}</td>
                      <td className="font-mono-num px-3 text-slate-500">{fmtDate(c.due_date)}</td>
                      <td className="font-mono-num px-3 text-right font-semibold text-slate-800">{brl(c.amount)}</td>
                      <td className="px-3"><ChargeBadge status={c.status} /></td>
                      <td className="whitespace-nowrap px-3 text-right">
                        {c.status !== "pago" && <Button size="sm" variant="ghost" className="h-8 text-sky-700 hover:bg-sky-50" onClick={() => setPayCharge(c)} data-testid={`pay-charge-${c.id}`}>Dar baixa</Button>}
                        <button onClick={() => del(`/charges/${c.id}`, "Excluir esta parcela?")} className="p-1 text-slate-300 hover:text-rose-500" data-testid={`delete-charge-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="surface p-5">
          <h3 className="font-display mb-3 text-lg font-bold text-slate-900">Pagamentos recebidos</h3>
          <div className="space-y-2" data-testid="payments-list">
            {payments.length === 0 && <Empty>Nenhum pagamento</Empty>}
            {payments.map((pay) => (
              <div key={pay.id} className="flex items-center justify-between gap-2 rounded-xl border border-slate-100 px-3 py-2.5" data-testid={`payment-row-${pay.id}`}>
                <div>
                  <p className="font-mono-num text-sm font-bold text-emerald-700">{brl(pay.amount)}</p>
                  <p className="text-xs text-slate-500">{fmtDate(pay.date)} · {METHODS[pay.method]}</p>
                </div>
                <div className="flex">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => printReceipt(p, pay, s)} data-testid={`receipt-${pay.id}`}><Receipt className="h-4 w-4 text-sky-600" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => del(`/payments/${pay.id}`, "Estornar/excluir este pagamento?")} data-testid={`delete-payment-${pay.id}`}><Trash2 className="h-4 w-4 text-slate-300" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <PaymentDialog open={payCharge !== undefined} onOpenChange={(v) => !v && setPayCharge(undefined)} patientId={p.id} charge={payCharge} />
      <PlanDialog open={plan} onOpenChange={setPlan} patientId={p.id} />
      <WhatsAppDialog open={wa} onOpenChange={setWa} name={p.name} phone={p.phone} template="cobranca"
        vars={{ valor: brl(summary.overdue), vencimento: nextOpen ? fmtDate(nextOpen.due_date) : "" }} />
    </div>
  );
};
